import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Zap, Globe, Smartphone, Check } from 'lucide-react';

interface WelcomeAnnouncementProps {
  currentLang: 'en' | 'fr' | 'de' | 'it';
}

const translations = {
  en: {
    badge: 'NEW',
    title: 'Welcome to the new TTStats',
    subtitle: 'Completely rebuilt from the ground up: faster, cleaner, and packed with new features',
    features: [
      'Login & sync favorites with mobile app',
      'Advanced set analysis & statistics',
      'Lightning-fast performance',
      'Many more features coming soon',
    ],
    cta: 'Got it',
  },
  de: {
    badge: 'NEU',
    title: 'Willkommen bei der neuen TTStats',
    subtitle: 'Von Grund auf neu entwickelt: schneller, sauberer und voller neuer Funktionen',
    features: [
      'Login & Favoriten mit App synchronisieren',
      'Erweiterte Satzanalyse & Statistiken',
      'Blitzschnelle Performance',
      'Viele weitere Features folgen',
    ],
    cta: 'Verstanden',
  },
  fr: {
    badge: 'NOUVEAU',
    title: 'Bienvenue sur la nouvelle version de TTStats',
    subtitle: 'Entièrement reconstruit: plus rapide, plus propre et plein de nouvelles fonctionnalités',
    features: [
      'Connexion & sync favoris avec l\'app',
      'Analyse de sets & statistiques avancées',
      'Performances ultra-rapides',
      'Beaucoup d\'autres fonctionnalités à venir',
    ],
    cta: 'Compris',
  },
  it: {
    badge: 'NUOVO',
    title: 'Benvenuti alla nuova versione di TTStats',
    subtitle: 'Completamente ricostruito: più veloce, più pulito e pieno di nuove funzionalità',
    features: [
      'Login & sincronizza preferiti con l\'app',
      'Analisi set & statistiche avanzate',
      'Prestazioni fulminee',
      'Molte altre funzionalità in arrivo',
    ],
    cta: 'Capito',
  },
};

const DISMISSED_KEY = 'ttstats_welcome_dismissed_v2';

export const WelcomeAnnouncement: React.FC<WelcomeAnnouncementProps> = ({ currentLang }) => {
  const [isVisible, setIsVisible] = useState(false);
  const t = translations[currentLang];

  useEffect(() => {
    // Check if user has already seen the announcement
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (!dismissed) {
      // Show after 2 seconds
      const id = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(id);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  if (!isVisible) return null;

  const features = [
    { icon: Zap, text: t.features[0] },
    { icon: Sparkles, text: t.features[1] },
    { icon: Smartphone, text: t.features[2] },
    { icon: Globe, text: t.features[3] },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[100] transition-colors duration-300"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="relative w-full max-w-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Content */}
              <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden transition-colors duration-300">
                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="absolute top-6 right-6 z-10 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-colors duration-300" />
                </button>

                <div className="relative p-8 md:p-12">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 mb-6 transition-colors duration-300">
                    <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 transition-colors duration-300" />
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 tracking-wide transition-colors duration-300">{t.badge}</span>
                  </div>

                  {/* Title */}
                  <h2 className="text-3xl md:text-4xl font-extralight text-gray-900 dark:text-gray-100 mb-4 leading-tight transition-colors duration-300">
                    {t.title}
                  </h2>

                  {/* Subtitle */}
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 font-light leading-relaxed transition-colors duration-300">
                    {t.subtitle}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center transition-colors duration-300">
                          <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 transition-colors duration-300" />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 transition-colors duration-300">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={handleDismiss}
                    className="w-full py-4 px-6 rounded-2xl font-medium text-white bg-[#1C7AD6] hover:bg-[#1562b3] dark:bg-[#1565C0] dark:hover:bg-[#0d47a1] transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] shadow-lg hover:shadow-xl"
                  >
                    {t.cta}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
